import React, { useCallback, useMemo } from 'react';
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import Spinner from 'components/Spinner';
import allAssetsImage from '../../assets/AT.png';
import styles from './LineChart.module.sass';


interface DataPoint {
  timestamp: string;
  quote: number;
  originalTimestamp?: string;
  adjustedDate?: string; // Added for timezone fix
}

interface TrendData {
  isPositive: boolean;
  rateOfChange: number | null;
  firstValue: number;
  lastValue: number;
}

interface AreaChartProps {
  title?: string;
  showChange?: boolean;
  showFilters?: boolean;
  data: DataPoint[];
  dataLabel?: string;
  loadingState?: boolean;
  flatYAxis?: boolean;
  duration?: string;
  onDaysChange?: (days: number) => void;
  yAxisDomain: [number, number];
  trendData: TrendData;
  formatTooltipDate?: (timestamp: string) => string;
  onRefresh?: () => void;
}

const defaultTrendData: TrendData = {
  isPositive: false,
  rateOfChange: 0,
  firstValue: 0,
  lastValue: 0
};

const AreaChart: React.FC<AreaChartProps> = ({
  title,
  showChange = false,
  showFilters = false,
  data,
  dataLabel = '',
  loadingState = false,
  flatYAxis = false,
  duration,
  onDaysChange,
  yAxisDomain,
  trendData = defaultTrendData,
  formatTooltipDate,
  onRefresh
}) => {
  // Add adjusted dates to compensate for timezone issues
  const processedData = useMemo(() => {
    return data.map(item => {
      const dateObj = new Date(item.timestamp);
      dateObj.setDate(dateObj.getDate() + 1); // Add one day to fix timezone offset
      return {
        ...item,
        adjustedDate: dateObj.toISOString().split('T')[0]
      };
    });
  }, [data]);

  const formatYAxis = useCallback((value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  }, []);

  const formatXAxis = useCallback((value: string) => {
    try {
      const date = new Date(value);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date encountered');
        return 'Invalid';
      }
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting X axis date');
      return 'Error';
    }
  }, []);

  const chartColors = useMemo(() => {
    // Enhanced color palette
    return {
      stroke: trendData.isPositive ? "#73F6FF" : "#F121C3",
      gradientId: trendData.isPositive ? "enhancedPositive" : "enhancedNegative",
      accentColor: trendData.isPositive ? "#00DAFF" : "#FF00C7"
    };
  }, [trendData.isPositive]);

  const chartConfig = {
    margin: { top: 20, right: 30, left: 40, bottom: 20 },
    tooltipStyle: {
      backgroundColor: '#1B1B24',
      border: '1px solid #2A2A35',
      borderRadius: '8px',
      boxShadow: '0 4px 14px rgba(0,0,0,0.4)'
    }
  };

  const formatTooltipLabel = useCallback((label: string) => {
    if (formatTooltipDate) {
      return formatTooltipDate(label);
    }
    
    try {
      const date = new Date(label);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid tooltip date');
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting tooltip date');
      return 'Error';
    }
  }, [formatTooltipDate]);

  const formatTooltipValue = useCallback((value: number) => {
    return [value.toLocaleString(), dataLabel || 'Value'];
  }, [dataLabel]);

  // Format the percentage change for display
  const formattedChangeValue = useMemo(() => {
    if (trendData.rateOfChange === null) return '0.00%';
    return `${Math.abs(trendData.rateOfChange).toFixed(2)}%`;
  }, [trendData.rateOfChange]);

  const ChangeIndicator = showChange && trendData.rateOfChange !== null ? (
    <div className={`${styles.changeContainer} ${trendData.isPositive ? styles.positiveChange : styles.negativeChange}`}>
      {trendData.isPositive ? (
        <>
          <ArrowUpwardIcon className={styles.changeIcon} />
          <span className={styles.changeValue}>{formattedChangeValue}</span>
        </>
      ) : (
        <>
          <ArrowDownwardIcon className={styles.changeIcon} />
          <span className={styles.changeValue}>{formattedChangeValue}</span>
        </>
      )}
    </div>
  ) : null;

  if (loadingState) {
    return (
      <div className={styles.chartContainer}>
        <div className={styles.chartHeader}>
          {title && <div className={styles.title}>{title}</div>}
        </div>
        <Spinner />
      </div>
    );
  }


  // Handle empty data with a refresh option
  // if (!processedData?.length) {
  if (!processedData || !Array.isArray(processedData) || processedData.length === 0) {
    return (
      <div className={styles.chartContainer}>
        <div className={styles.emptyStateContainer}>
          <div className={styles.emptyStateIcon}>
            <RefreshIcon style={{ fontSize: 28 }} />
          </div>
          <h3 className={styles.emptyStateTitle}>No Chart Data</h3>
          <p className={styles.emptyStateDescription}>
            Failed to load chart data, please try again.
          </p>
          <button onClick={onRefresh} className={styles.refreshButton}>
            Refresh Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeader}>
        {title && <div className={styles.title}>{title}</div>}
        {ChangeIndicator}
      </div>
      <div className={styles.innerContainer}>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsAreaChart 
            data={processedData} 
            margin={chartConfig.margin}
            animationDuration={2000}
            animationEasing="ease-out"
          >
            <defs>
              <pattern id="backgroundImage" patternUnits="userSpaceOnUse" width="100%" height="100%">
                <image 
                  href={allAssetsImage}
                  x="27%"
                  y="15%"
                  width="45%"
                  height="45%"
                  opacity="0.07"
                  preserveAspectRatio="xMidYMid meet"
                  transform="translate(-30%, -30%)"
                />
              </pattern>
              
              {/* Enhanced gradients for positive trend */}
              <linearGradient id="enhancedPositive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#73F6FF" stopOpacity={0.9} />
                <stop offset="40%" stopColor="#73F6FF" stopOpacity={0.4} />
                <stop offset="85%" stopColor="#73F6FF" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#73F6FF" stopOpacity={0} />
              </linearGradient>
              
              <linearGradient id="positiveStrokeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#00C8FF" />
                <stop offset="50%" stopColor="#73F6FF" />
                <stop offset="100%" stopColor="#00F0FF" />
              </linearGradient>
              
              {/* Enhanced gradients for negative trend */}
              <linearGradient id="enhancedNegative" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F121C3" stopOpacity={0.9} />
                <stop offset="40%" stopColor="#F121C3" stopOpacity={0.4} />
                <stop offset="85%" stopColor="#F121C3" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#F121C3" stopOpacity={0} />
              </linearGradient>
              
              <linearGradient id="negativeStrokeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#D800A6" />
                <stop offset="50%" stopColor="#F121C3" />
                <stop offset="100%" stopColor="#FF00E5" />
              </linearGradient>
              
              {/* Glow filter for dots */}
              <filter id="glow" height="200%" width="200%" x="-50%" y="-50%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feFlood 
                  floodColor={chartColors.accentColor} 
                  floodOpacity="0.7" 
                  result="glow-color" 
                />
                <feComposite 
                  in="glow-color" 
                  in2="blur" 
                  operator="in" 
                  result="colored-blur" 
                />
                <feMerge>
                  <feMergeNode in="colored-blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            
            <rect width="100%" height="100%" fill="url(#backgroundImage)" />
            <CartesianGrid 
              strokeDasharray="3 5" 
              stroke="#2A2A35" 
              vertical={false} 
              opacity={0.7}
            />
            <XAxis 
              dataKey="adjustedDate"
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatXAxis}
              interval={0}
              axisLine={{ stroke: '#2A2A35', strokeWidth: 1 }}
              domain={['dataMin', 'dataMax']}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatYAxis}
              domain={flatYAxis ? yAxisDomain : ['auto', 'auto']}
              allowDecimals={false}
              width={60}
              axisLine={{ stroke: '#2A2A35', strokeWidth: 1 }}
            />
            <Tooltip
              cursor={{ 
                stroke: trendData.isPositive ? '#73F6FF' : '#F121C3', 
                strokeWidth: 1,
                strokeDasharray: '3 3',
                opacity: 0.5
              }}
              wrapperStyle={{ outline: 'none' }}
              contentStyle={{
                ...chartConfig.tooltipStyle,
                boxShadow: `0 2px 14px rgba(${trendData.isPositive ? '115, 246, 255' : '241, 33, 195'}, 0.15)`
              }}
              labelStyle={{ color: '#6B7280' }}
              itemStyle={{ color: chartColors.stroke, fontWeight: 500 }}
              labelFormatter={formatTooltipLabel}
              formatter={formatTooltipValue}
              animationDuration={300}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="quote"
              stroke={`url(#${trendData.isPositive ? 'positiveStrokeGradient' : 'negativeStrokeGradient'})`}
              strokeWidth={2.5}
              fill={`url(#${chartColors.gradientId})`}
              fillOpacity={1}
              activeDot={{ 
                r: 6, 
                fill: chartColors.accentColor,
                stroke: '#fff',
                strokeWidth: 2,
                filter: 'url(#glow)'
              }}
              animationDuration={2000}
              isAnimationActive={true}
            />
          </RechartsAreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default React.memo(AreaChart);